/**
 * The platform's refusals (ADR 0054): a suspended company, phone service still being set up,
 * outside calling not open yet, trial and daily limits, blocked destinations. The server sends
 * these sentences itself; the apps use the same ones when a refusal arrives without a body, and
 * treat every one as a refusal to show, never a failure to retry quickly. The sentences match
 * `vd_core::platform_providers::copy` and name no provider.
 */

export const PLATFORM_REFUSALS: Readonly<Record<string, string>> = {
  paused: "Your account is paused.",
  setting_up: "Your phone service is still being set up. Try again in a minute.",
  calling_not_open: "Calling opens soon.",
  not_verified: "Verify your business to start calling and texting.",
  outbound_stopped: "Calling is unavailable right now. Try again later.",
  trial_limit: "Your trial limit is reached. Choose a plan to keep going.",
  daily_limit: "Today's spending limit is reached. It resets at midnight UTC.",
  destination_blocked: "Kite can't call that number.",
  too_many_calls: "Too many calls at once. Try again when one ends.",
  call_rate: "Too many calls this hour. Try again later.",
};

const CODES: ReadonlySet<string> = new Set(Object.keys(PLATFORM_REFUSALS));

/** Whether an error code is one of the platform's refusals. */
export function isPlatformRefusal(code: string | null | undefined): boolean {
  return code != null && CODES.has(code);
}

/**
 * The sentence to show for a platform refusal: the server's own message when it sent one,
 * else the catalog's. `null` for any other error.
 */
export function platformRefusalMessage(
  code: string | null | undefined,
  serverMessage?: string,
): string | null {
  if (!isPlatformRefusal(code)) return null;
  const m = serverMessage?.trim();
  return m ? m : (PLATFORM_REFUSALS[code as string] ?? null);
}

/**
 * How long the phone waits before asking for a calling token again after a refusal: a paused
 * account or closed calling waits five minutes, a setup in progress one minute. `null` for an
 * error that is not a platform refusal (the caller keeps its own retry).
 */
export function refusalRetryMs(code: string | null | undefined): number | null {
  if (!isPlatformRefusal(code)) return null;
  return code === "setting_up" ? 60_000 : 300_000;
}
